import { useCallback, useMemo } from "react";
import { getDirectoryPageSize } from "@/utils/directoryQuery";
import { SYMPTOM_DOMAIN_MAP } from "@/utils/constants";

const scrollToListings = () => document.getElementById("listings-section")?.scrollIntoView({ behavior: "smooth" });

function domainsForQuery(query) {
  const domains = new Set();
  Object.entries(SYMPTOM_DOMAIN_MAP).forEach(([keyword, values]) => {
    if (query.includes(keyword)) values.forEach((value) => domains.add(value));
  });
  return [...domains];
}

function withoutDirectoryParams(searchParams) {
  const params = new URLSearchParams(searchParams);
  ["category", "county", "details", "domain", "grade", "proof", "q", "radius", "zip", "zone"]
    .forEach((key) => params.delete(key));
  return params;
}

export function useDirectoryInteractions({
  loadNextPage,
  locationSource,
  searchParams,
  setSearchParams,
  setters,
  showFavsOnly,
  symptomQuery,
}) {
  const toggleCompare = useCallback((listing) => {
    setters.setCompareList((previous) => {
      if (previous.some((item) => item.id === listing.id)) return previous.filter((item) => item.id !== listing.id);
      return previous.length >= 3 ? previous : [...previous, listing];
    });
  }, [setters]);

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) { setters.setGeoStatus("denied"); return; }
    setters.setGeoStatus("loading");
    navigator.geolocation.getCurrentPosition((position) => {
      setters.setNearbyZip(null);
      setters.setUserLocation({
        lat: Number(position.coords.latitude.toFixed(3)),
        lng: Number(position.coords.longitude.toFixed(3)),
      });
      setters.setLocationSource("browser");
      setters.setGeoStatus("granted");
      setters.setSortBy("nearby");
    }, () => setters.setGeoStatus("denied"), { enableHighAccuracy: false, timeout: 8000 });
  }, [setters]);

  const loadMore = useCallback(() => {
    if (showFavsOnly) setters.setClientVisible((value) => value + getDirectoryPageSize());
    else loadNextPage();
  }, [loadNextPage, setters, showFavsOnly]);

  const handleSymptomSearch = useCallback(() => {
    const trimmed = symptomQuery.trim();
    if (!trimmed) { setters.setMatchedDomains([]); setters.setSearchQuery(""); return; }
    const matched = domainsForQuery(trimmed.toLowerCase());
    setters.setMatchedDomains(matched);
    setters.setSearchQuery(matched.length > 0 ? "" : trimmed);
    const params = new URLSearchParams(searchParams);
    if (matched.length > 0) params.delete("q");
    else params.set("q", trimmed);
    setSearchParams(params, { replace: true });
    window.setTimeout(scrollToListings, 100);
  }, [searchParams, setSearchParams, setters, symptomQuery]);

  const clearSymptomSearch = useCallback(() => {
    setters.setSymptomQuery("");
    setters.setMatchedDomains([]);
    setters.setSearchQuery("");
    setters.setActiveCategory("all");
    const params = new URLSearchParams(searchParams);
    params.delete("q");
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams, setters]);

  const handleGoalSelect = useCallback((goal, config) => {
    setters.setSymptomQuery(goal);
    setters.setMatchedDomains(config.domains);
    setters.setActiveCategory(config.categories.length === 1 ? config.categories[0] : "all");
    scrollToListings();
  }, [setters]);

  const applyNearby = useCallback((nearby) => {
    setters.setNearbyZip(nearby);
    setters.setActiveZone(null);
    setters.setActiveCounty(null);
    setters.setUserLocation({ lat: Number(nearby.lat.toFixed(3)), lng: Number(nearby.lng.toFixed(3)) });
    setters.setLocationSource("zip");
    setters.setGeoStatus("granted");
    setters.setSortBy("nearby");
  }, [setters]);

  const clearNearby = useCallback(() => {
    setters.setNearbyZip(null);
    setters.setNearbyRadius(null);
    if (locationSource !== "zip") return;
    setters.setUserLocation(null);
    setters.setLocationSource(null);
    setters.setGeoStatus("idle");
    setters.setSortBy("default");
  }, [locationSource, setters]);

  const clearFilters = useCallback(() => {
    setters.setActiveGrade(null); setters.setActiveZone(null); setters.setActiveDomain(null);
    setters.setActiveCategory("all"); setters.setActiveCounty(null); setters.setNearbyZip(null); setters.setNearbyRadius(null);
    setters.setSearchQuery(""); setters.setDetailsFilter("all"); setters.setMatchedDomains([]);
    setters.setUserLocation(null); setters.setLocationSource(null); setters.setGeoStatus("idle");
    setters.setSortBy("default");
    setSearchParams(withoutDirectoryParams(searchParams), { replace: true });
  }, [searchParams, setSearchParams, setters]);

  const locationActions = useMemo(() => ({
    applyNearby,
    clearNearby,
    requestLocation,
  }), [applyNearby, clearNearby, requestLocation]);
  const filterActions = useMemo(() => ({
    clearFilters,
    clearSymptomSearch,
    handleGoalSelect,
    handleSymptomSearch,
  }), [clearFilters, clearSymptomSearch, handleGoalSelect, handleSymptomSearch]);
  const listingActions = useMemo(() => ({
    loadMore,
    openReviewModal: (listing) => setters.setReviewModal({ open: true, listingName: listing?.name || "" }),
    toggleCompare,
  }), [loadMore, setters, toggleCompare]);
  return useMemo(() => ({
    ...filterActions,
    ...listingActions,
    ...locationActions,
  }), [filterActions, listingActions, locationActions]);
}